import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { trackPageEngagement } from '@/lib/analytics'

/**
 * Tracks scroll depth and time on page, fires a page_engagement GA4 event
 * when the user navigates away or hides the tab.
 */
export function usePageEngagement() {
  const location = useLocation()
  // Init to 0 and set in effect — calling performance.now() during render
  // violates React's purity rule (caught by react-hooks/purity lint).
  const startTime = useRef(0)
  const maxScroll = useRef(0)
  const fired = useRef(false)

  useEffect(() => {
    // Reset on route change
    startTime.current = performance.now()
    maxScroll.current = 0
    fired.current = false

    function updateScrollDepth() {
      const scrollTop = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      if (docHeight > 0) {
        const percent = Math.min(Math.round((scrollTop / docHeight) * 100), 100)
        if (percent > maxScroll.current) {
          maxScroll.current = percent
        }
      }
    }

    function fireEvent() {
      if (fired.current) return
      fired.current = true
      const timeOnPage = (performance.now() - startTime.current) / 1000
      trackPageEngagement(location.pathname, timeOnPage, maxScroll.current)
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        fireEvent()
      }
    }

    window.addEventListener('scroll', updateScrollDepth, { passive: true })
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', fireEvent)

    return () => {
      fireEvent()
      window.removeEventListener('scroll', updateScrollDepth)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', fireEvent)
    }
  }, [location.pathname])
}
